/**
 * @fileoverview 고객센터 문의글 등록 API 함수
 * 파일 업로드와 함께 1:1 문의글을 등록하는 기능을 제공합니다.
 * 회원/비회원 구분에 따라 다른 엔드포인트를 사용하여 문의글을 등록합니다.
 */

import { apiInstance } from "@core/api/axios/apiInstance";
import { GetArticleType } from "@features/helpdesk/model/getArticlesModel";
import { FileUploadResponse, PostArticlesType } from "@features/helpdesk/model/postArticlesModel";
import { postUploadFiles } from "@features/helpdesk/api/postUploadFile";

/**
 * 문의글 등록을 위한 요청 데이터를 구성합니다.
 *
 * 사용자 입력 데이터로부터 API 요청에 필요한 형태로 데이터를 변환하며,
 * 비회원 정보(동의, reCAPTCHA 관련)는 조건부로 포함됩니다.
 *
 * @param {PostArticlesType} payload - 사용자가 입력한 문의글 데이터
 * @returns {Record<string, any>} API 요청용으로 구성된 데이터 객체
 */
const buildRequestData = (payload: PostArticlesType): Record<string, any> => {
  return {
    category: payload.category,
    pcbangName: payload.pcbangName,
    userName: payload.userName,
    phone: payload.phone,
    email: payload.email,
    title: payload.title,
    contents: payload.contents,
    fileUpload: (payload.fileUpload?.length ?? 0) > 0,
    // 비회원 정보
    ...(payload.agree !== undefined && { agree: payload.agree }),
    ...(payload.recaptchaSiteKey && { recaptchaSiteKey: payload.recaptchaSiteKey }),
    ...(payload.recaptchaToken && { recaptchaToken: payload.recaptchaToken }),
  };
};

/**
 * 파일 업로드 응답 데이터를 문의글 등록 요청 데이터에 추가합니다.
 *
 * 업로드된 파일들의 이름을 fileName1, fileName2, fileName3 형태로
 * 동적으로 요청 데이터에 추가합니다.
 *
 * @param {Record<string, any>} articleData - 문의글 등록 요청 데이터 (원본 객체가 수정됨)
 * @param {FileUploadResponse[]} fileUploadResponses - 파일 업로드 응답 배열
 * @returns {void}
 */
const addFileNamesToRequest = (articleData: Record<string, any>, fileUploadResponses: FileUploadResponse[]): void => {
  fileUploadResponses.forEach((response, index) => {
    articleData[`fileName${index + 1}`] = response.uploadFileName;
  });
};

/**
 * 로그인 상태에 따라 적절한 API 엔드포인트 URL을 결정합니다.
 *
 * @param {boolean} isLogin - 사용자 로그인 상태
 * @returns {string} API 엔드포인트 URL
 *   - 로그인 사용자: "question/articles"
 *   - 비회원 사용자: "question/guest-articles"
 */
const getApiUrl = (isLogin: boolean): string => {
  return isLogin ? "question/articles" : "question/guest-articles";
};

/**
 * 문의글을 등록하는 API 함수입니다.
 *
 * 파일 업로드가 있는 경우 먼저 파일을 업로드한 후,
 * 문의글 등록 데이터를 구성하여 서버에 전송합니다.
 * 로그인 상태에 따라 다른 엔드포인트를 사용합니다.
 *
 * @async
 * @function postArticle
 * @param {PostArticlesType} payload - 문의글 등록에 필요한 사용자 입력 데이터
 * @param {boolean} isLogin - 사용자의 로그인 상태
 * @returns {Promise<GetArticleType>} 등록된 문의글 정보
 * @throws {Error} 파일 업로드 실패 또는 문의글 등록 실패 시 발생
 */
export const postArticle = async (payload: PostArticlesType, isLogin: boolean): Promise<GetArticleType> => {
  try {
    let fileUploadResponses: FileUploadResponse[] | null = null;

    // 파일이 있으면 먼저 파일 업로드
    if (payload.fileUpload && payload.fileUpload.length > 0) {
      fileUploadResponses = await postUploadFiles(payload.fileUpload);
    }

    // 문의글 등록 데이터 구성
    const articleData = buildRequestData(payload);

    // 파일 업로드 응답이 있으면 fileName1, fileName2, fileName3 동적 추가
    if (fileUploadResponses && fileUploadResponses.length > 0) {
      addFileNamesToRequest(articleData, fileUploadResponses);
    }

    const url = getApiUrl(isLogin);
    const response = await apiInstance.post(url, articleData);

    return response.data;
  } catch (error) {
    console.error("문의 등록 실패:", error);
    throw error;
  }
};
